import { reactive } from 'vue';
import { logout as performLogout } from '@/utils/auth';
import { isAuthenticatedSession } from '@/utils/sessionAuth';
import {
  INACTIVITY_WARNING_AFTER_MS,
  INACTIVITY_GRACE_PERIOD_SEC,
} from '@/utils/inactivityConfig';
import { routeRecordIsProtected } from '@/router/routeMeta';

const InactivityStore = reactive({
  isActive: false,
  showWarning: false,
  timeoutId: null,
  secondsRemaining: INACTIVITY_GRACE_PERIOD_SEC,
  intervalId: null
});

const InactivityPlugin = {
  install(app, options = {}) {
    const router = options.router;
    
    if (!router) {
      console.error('InactivityPlugin requires router option');
      return;
    }

    const INACTIVITY_WARNING_TIME = INACTIVITY_WARNING_AFTER_MS;
    const INACTIVITY_LOGOUT_TIME =
      INACTIVITY_WARNING_TIME + INACTIVITY_GRACE_PERIOD_SEC * 1000;

    /** Évite d’empiler plusieurs fois mousedown/keydown/… à chaque navigation */
    let activityListenersAttached = false;

    let inactivityTimer = null;
    let logoutTimeoutId = null; // timer de déconnexion après l'avertissement
    let warningShownAt = null;

    const resetInactivityTimer = () => {
      // Réinitialiser les timers
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      if (InactivityStore.intervalId) {
        clearInterval(InactivityStore.intervalId);
        InactivityStore.intervalId = null;
      }
      if (logoutTimeoutId) {
        clearTimeout(logoutTimeoutId);
        logoutTimeoutId = null;
      }

      InactivityStore.showWarning = false;
      InactivityStore.secondsRemaining = INACTIVITY_GRACE_PERIOD_SEC;

      // Seul les utilisateurs connectés activent l'inactivité
      if (!isAuthenticatedSession()) return;

      // Timer pour afficher l'avertissement
      inactivityTimer = setTimeout(() => {
        InactivityStore.showWarning = true;
        warningShownAt = Date.now();

        // Démarrer le compteur à rebours
        InactivityStore.secondsRemaining = INACTIVITY_GRACE_PERIOD_SEC;
        InactivityStore.intervalId = setInterval(() => {
          InactivityStore.secondsRemaining--;
          if (InactivityStore.secondsRemaining <= 0) {
            clearInterval(InactivityStore.intervalId);
            InactivityStore.intervalId = null;
            logoutDueToInactivity();
          }
        }, 1000);

        // Filet de sécurité : même si l’intervalle est bloqué, déconnexion après la période de grâce
        logoutTimeoutId = setTimeout(() => {
          logoutTimeoutId = null;
          logoutDueToInactivity();
        }, INACTIVITY_LOGOUT_TIME - INACTIVITY_WARNING_TIME);
      }, INACTIVITY_WARNING_TIME);
    };

    /** Route interne à réouvrir après reconnexion (évite redirect=/ ou page login). */
    function getSafePathForReauth() {
      try {
        const full = router?.currentRoute?.value?.fullPath || '';
        if (!full || full === '/' || full === '/connexion' || full.startsWith('/connexion?')) {
          return '';
        }
        return full;
      } catch {
        return '';
      }
    }

    /** Fin de session après délai d’inactivité : redirection /connexion?reason=inactivity (bandeau sur la page login, sans alert()) */
    const logoutDueToInactivity = () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
        inactivityTimer = null;
      }
      if (InactivityStore.intervalId) {
        clearInterval(InactivityStore.intervalId);
        InactivityStore.intervalId = null;
      }
      if (logoutTimeoutId) {
        clearTimeout(logoutTimeoutId);
        logoutTimeoutId = null;
      }
      InactivityStore.showWarning = false;

      const back = getSafePathForReauth();
      const query = back
        ? { reason: 'inactivity', redirect: back }
        : { reason: 'inactivity' };

      performLogout(router, null, false, true, query);
    };

    /** Déconnexion volontaire depuis la modale (« Me déconnecter ») — pas de message « inactivité » */
    const logoutManual = () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
        inactivityTimer = null;
      }
      if (InactivityStore.intervalId) {
        clearInterval(InactivityStore.intervalId);
        InactivityStore.intervalId = null;
      }
      if (logoutTimeoutId) {
        clearTimeout(logoutTimeoutId);
        logoutTimeoutId = null;
      }
      InactivityStore.showWarning = false;

      performLogout(router);
    };

    const extendSession = () => {
      InactivityStore.showWarning = false;
      if (InactivityStore.intervalId) {
        clearInterval(InactivityStore.intervalId);
      }
      resetInactivityTimer();
    };

    // Événements d’activité (lecture longue : peu de keydown ; souris limitée → throttle dédié)
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'wheel'];

    let lastMouseResetTs = 0;
    const MOUSE_MOVE_THROTTLE_MS = 4000;
    const onMouseMoveThrottled = () => {
      const n = Date.now();
      if (n - lastMouseResetTs < MOUSE_MOVE_THROTTLE_MS) return;
      lastMouseResetTs = n;
      resetInactivityTimer();
    };

    const setupListeners = () => {
      if (activityListenersAttached) return;
      activityEvents.forEach((event) => {
        document.addEventListener(event, resetInactivityTimer, true);
      });
      document.addEventListener('mousemove', onMouseMoveThrottled, true);
      activityListenersAttached = true;
    };

    const removeListeners = () => {
      if (!activityListenersAttached) {
        if (inactivityTimer) {
          clearTimeout(inactivityTimer);
          inactivityTimer = null;
        }
        if (InactivityStore.intervalId) {
          clearInterval(InactivityStore.intervalId);
          InactivityStore.intervalId = null;
        }
        if (logoutTimeoutId) {
          clearTimeout(logoutTimeoutId);
          logoutTimeoutId = null;
        }
        return;
      }
      activityEvents.forEach((event) => {
        document.removeEventListener(event, resetInactivityTimer, true);
      });
      document.removeEventListener('mousemove', onMouseMoveThrottled, true);
      activityListenersAttached = false;
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
        inactivityTimer = null;
      }
      if (InactivityStore.intervalId) {
        clearInterval(InactivityStore.intervalId);
        InactivityStore.intervalId = null;
      }
      if (logoutTimeoutId) {
        clearTimeout(logoutTimeoutId);
        logoutTimeoutId = null;
      }
    };

    // Exposer les méthodes et l'état globalement
    app.config.globalProperties.$inactivity = {
      store: InactivityStore,
      logoutDueToInactivity,
      logoutManual,
      extendSession,
      setupListeners,
      removeListeners
    };

    // Nettoyer quand on quitte l'app
    router.afterEach((to) => {
      const token = isAuthenticatedSession();
      
      // Routes protégées : auth, rôle, ou permissions (aligné sur routes.js)
      if (to.matched.some((record) => routeRecordIsProtected(record))) {
        if (token) {
          setupListeners();
          resetInactivityTimer();
        } else {
          removeListeners();
        }
      } else {
        removeListeners();
      }
    });

    // Initialiser au démarrage
    if (isAuthenticatedSession()) {
      setupListeners();
      resetInactivityTimer();
    }
  }
};

export default InactivityPlugin;
